import { useEffect, useRef, useState } from "react";
import { Clock, X } from "lucide-react";
import { pad2 } from "./datePickerUtils";

const HOURS = Array.from({ length: 24 }, (_, i) => pad2(i));
const MINUTES = Array.from({ length: 60 }, (_, i) => pad2(i));

const INPUT_CLASS =
  "flex items-center gap-2 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white text-left hover:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500";

function parseTime(value) {
  if (!value) return { hour: "09", minute: "00" };
  const [h, m] = String(value).split(":");
  return { hour: pad2(Number(h) || 0), minute: pad2(Number(m) || 0) };
}

function formatTimeDisplay(value) {
  if (!value) return "";
  const [h, m] = value.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return value;
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", hour12: true });
}

const QUICK_TIMES = ["09:00", "09:30", "13:00", "18:00", "18:30"];

export default function TimePickerField({
  label,
  value = "",
  onChange,
  required = false,
  placeholder = "Pick time",
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const { hour, minute } = parseTime(value);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const apply = (h, m) => {
    onChange?.(`${h}:${m}`);
  };

  const pickQuick = (t) => {
    onChange?.(t);
    setOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={rootRef}>
      {label && (
        <label className="block text-sm font-medium text-slate-700 mb-1">
          {label}
          {required ? " *" : ""}
        </label>
      )}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`${INPUT_CLASS} ${!value ? "text-slate-400" : "text-slate-800"}`}
      >
        <Clock className="w-4 h-4 text-teal-600 shrink-0" />
        <span className="flex-1 truncate">{value ? formatTimeDisplay(value) : placeholder}</span>
        {value && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onChange?.("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.stopPropagation();
                onChange?.("");
              }
            }}
            className="p-0.5 rounded hover:bg-slate-100 text-slate-400"
            aria-label="Clear time"
          >
            <X className="w-3.5 h-3.5" />
          </span>
        )}
      </button>

      {open && (
        <div className="absolute z-50 mt-2 left-0 bg-white rounded-2xl border border-slate-200 shadow-xl p-4 w-[260px]">
          <p className="text-xs font-medium text-slate-500 mb-2">Quick select</p>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {QUICK_TIMES.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => pickQuick(t)}
                className={`text-xs px-2.5 py-1 rounded-lg border transition ${
                  value === t
                    ? "bg-teal-600 text-white border-teal-600"
                    : "border-slate-200 text-slate-600 hover:border-teal-300 hover:bg-teal-50"
                }`}
              >
                {formatTimeDisplay(t)}
              </button>
            ))}
          </div>

          <div className="flex gap-2">
            <div className="flex-1">
              <p className="text-[11px] font-medium text-slate-400 mb-1 text-center">Hour</p>
              <div className="h-36 overflow-y-auto rounded-lg border border-slate-100 bg-slate-50/50 p-1 space-y-0.5">
                {HOURS.map((h) => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => apply(h, minute)}
                    className={`w-full py-1.5 rounded-md text-sm font-medium transition ${
                      hour === h
                        ? "bg-teal-600 text-white"
                        : "text-slate-700 hover:bg-teal-50"
                    }`}
                  >
                    {h}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex-1">
              <p className="text-[11px] font-medium text-slate-400 mb-1 text-center">Minute</p>
              <div className="h-36 overflow-y-auto rounded-lg border border-slate-100 bg-slate-50/50 p-1 space-y-0.5">
                {MINUTES.map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => apply(hour, m)}
                    className={`w-full py-1.5 rounded-md text-sm font-medium transition ${
                      minute === m
                        ? "bg-teal-600 text-white"
                        : "text-slate-700 hover:bg-teal-50"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-between gap-2 mt-3 pt-3 border-t border-slate-100">
            <button
              type="button"
              onClick={() => onChange?.("")}
              className="text-xs font-medium text-slate-500 hover:text-slate-700 px-2 py-1 rounded-lg hover:bg-slate-50"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => {
                const now = new Date();
                onChange?.(`${pad2(now.getHours())}:${pad2(now.getMinutes())}`);
                setOpen(false);
              }}
              className="text-xs font-medium text-teal-700 px-3 py-1 rounded-lg bg-teal-50 hover:bg-teal-100"
            >
              Now
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs font-medium text-white px-3 py-1 rounded-lg bg-teal-600 hover:bg-teal-700"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
